import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cn } from "cn";
import type * as React from "react";
import { TabsIndicator, useTabsIndicator } from "./tabs-indicator";

function Tabs({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col", className)}
      {...props}
    />
  );
}

function TabsList({
  className,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List>) {
  const { listRef, indicatorRef } = useTabsIndicator<HTMLDivElement>();

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      className={cn(
        /* sorted */
        "relative inline-flex items-center justify-start",
        "h-8 w-full",
        "p-0.5",
        "text-text-dark-min",
        "bg-bg-dark-min rounded-full",
        className,
      )}
      {...props}
    >
      <TabsIndicator
        ref={indicatorRef}
        className="bg-bg-light border border-border-dark-high rounded-full shadow-sm"
      />
      {children}
    </TabsPrimitive.List>
  );
}

function TabsTrigger({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        /* sorted */
        "relative flex items-center justify-center gap-1.5",
        "h-[calc(100%-1px)]",
        "px-4 py-2",
        "text-sm text-text-dark-high whitespace-nowrap",
        "border border-transparent rounded-full cursor-pointer",
        "transition-[color,box-shadow]",
        "hover:text-text-dark-max",
        "data-[state=active]:text-text-dark-max",
        "focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
